import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Users,
  Search,
  MoreVertical,
  Mail,
  Link2,
  UserX,
  UserCheck,
  Trash2,
  Edit,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Building2,
  Briefcase,
  HardHat,
  Loader2,
  Plus,
  ArrowUpDown,
  Shield,
  FolderOpen,
  Key,
  Eye,
  EyeOff,
  Send,
  X,
  FolderPlus,
} from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';

// Status configuration
const statusConfig = {
  pending: { label: 'Pending Invite', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  expired: { label: 'Invite Expired', color: 'bg-orange-100 text-orange-800', icon: AlertTriangle },
  active: { label: 'Active', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  disabled: { label: 'Disabled', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const teamTypeConfig = {
  admin: { label: 'Admin', color: 'bg-red-100 text-red-800', icon: Shield },
  professional: { label: 'Professional Team', color: 'bg-blue-100 text-blue-800', icon: Briefcase },
  internal: { label: 'Internal Team', color: 'bg-purple-100 text-purple-800', icon: Building2 },
  subcontractor: { label: 'Subcontractor', color: 'bg-amber-100 text-amber-800', icon: HardHat },
};

// Sortable Header Component
const SortableHeader = ({ label, field, sortBy, sortOrder, onSort }) => {
  const isActive = sortBy === field;
  return (
    <th 
      className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:bg-slate-100 select-none"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        <span className="inline-flex flex-col">
          {isActive ? (
            sortOrder === 'asc' ? (
              <ChevronUp className="h-3 w-3 text-slate-900" />
            ) : (
              <ChevronDown className="h-3 w-3 text-slate-900" />
            )
          ) : (
            <ArrowUpDown className="h-3 w-3 text-slate-300" />
          )}
        </span>
      </div>
    </th>
  );
};

const AdminUserManagement = () => {
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [roles, setRoles] = useState([]);
  
  // Main tab state
  const [activeTab, setActiveTab] = useState('all');
  
  // Team requests (awaiting approval)
  const [teamRequests, setTeamRequests] = useState([]);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  
  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Selection for bulk actions
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // Dialogs
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [inviteLinkDialog, setInviteLinkDialog] = useState({ open: false, link: '' });
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [projectsDialogOpen, setProjectsDialogOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [bulkResetDialogOpen, setBulkResetDialogOpen] = useState(false);
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  
  // Expanded projects (for grouped view)
  const [expandedProjects, setExpandedProjects] = useState({});
  const [viewMode, setViewMode] = useState('list');
  const [groupedData, setGroupedData] = useState(null);
  
  useEffect(() => {
    loadInitialData();
  }, []);
  
  useEffect(() => {
    loadDataForTab();
  }, [search, statusFilter, teamFilter, projectFilter, roleFilter, sortBy, sortOrder, page, activeTab, viewMode]);
  
  const loadInitialData = async () => {
    try {
      const [statsRes, projectsRes, rolesRes, pendingCountRes] = await Promise.allSettled([
        api.adminUsers.getStats(),
        api.projects.getAll(),
        api.get('/roles'),
        api.teamRequests.getPendingCount()
      ]);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (projectsRes.status === 'fulfilled') setProjects(projectsRes.value.data || []);
      if (rolesRes.status === 'fulfilled') setRoles(rolesRes.value.data || []);
      if (pendingCountRes.status === 'fulfilled') setPendingRequestCount(pendingCountRes.value.data.count || 0);
    } catch (err) {
      console.error('Failed to load initial data:', err);
    }
  };
  
  const loadDataForTab = async () => {
    if (activeTab === 'approval') {
      await loadTeamRequests();
    } else if (viewMode === 'grouped') {
      await loadGroupedUsers();
    } else {
      await loadUsers();
    }
  };
  
  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 50,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (teamFilter !== 'all') params.team_type = teamFilter;
      if (projectFilter !== 'all') params.project_id = projectFilter;
      if (roleFilter !== 'all') params.role = roleFilter;
      
      // Override status filter based on tab
      if (activeTab === 'pending') params.status = 'pending';
      else if (activeTab === 'deactivated') params.status = 'disabled';
      else if (activeTab === 'expired') params.status = 'expired';
      
      const res = await api.adminUsers.getAll(params);
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      toast.error('Failed to load users');
    }
    setLoading(false);
  };
  
  const loadGroupedUsers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (teamFilter !== 'all') params.team_type = teamFilter;
      
      const res = await api.adminUsers.getGroupedByProject(params);
      setGroupedData(res.data);
    } catch (err) {
      toast.error('Failed to load grouped users');
    }
    setLoading(false);
  };
  
  const loadTeamRequests = async () => {
    setLoading(true);
    try {
      const res = await api.teamRequests.getAll({ status: 'pending' });
      setTeamRequests(res.data.requests || []);
    } catch (err) {
      toast.error('Failed to load team requests');
    }
    setLoading(false);
  };
  
  const refreshData = () => {
    loadInitialData();
    loadDataForTab();
  };
  
  // User Actions
  const handleResendInvite = async (userId) => {
    try {
      await api.invitations.resend(userId);
      toast.success('Invitation resent successfully');
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invitation');
    }
  };
  
  const handleCopyInviteLink = async (userId) => {
    try {
      const res = await api.invitations.getLink(userId);
      setInviteLinkDialog({ open: true, link: res.data.invite_link });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to get invite link');
    }
  };
  
  const handleDisableUser = async (userId) => {
    if (!window.confirm('Are you sure you want to disable this user?')) return;
    try {
      await api.adminUsers.disable(userId);
      toast.success('User disabled');
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disable user');
    }
  };
  
  const handleEnableUser = async (userId) => {
    try {
      await api.adminUsers.enable(userId);
      toast.success('User enabled');
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to enable user');
    }
  };
  
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to permanently delete this user? This action cannot be undone.')) return;
    try {
      await api.adminUsers.delete(userId);
      toast.success('User deleted');
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };
  
  const handleResetPassword = async (userId) => {
    try {
      await api.adminUsers.resetPassword(userId);
      toast.success('Password reset email sent');
      setResetPasswordDialogOpen(false);
      setSelectedUser(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset email');
    }
  };
  
  // Bulk Actions
  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }
    
    const actionLabels = {
      disable: 'disable',
      enable: 'enable',
      delete: 'permanently delete'
    };
    
    if (!window.confirm(`Are you sure you want to ${actionLabels[action]} ${selectedUsers.length} user(s)?`)) return;
    
    try {
      await api.adminUsers.bulkAction({ user_ids: selectedUsers, action });
      toast.success(`Bulk ${action} completed`);
      setSelectedUsers([]);
      refreshData();
    } catch (err) {
      toast.error('Bulk action failed');
    }
  };
  
  const handleBulkResetPassword = async () => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }
    
    try {
      let successCount = 0;
      for (const userId of selectedUsers) {
        try {
          await api.adminUsers.resetPassword(userId);
          successCount++;
        } catch (e) {
          console.error(`Failed to reset password for ${userId}`);
        }
      }
      toast.success(`Password reset emails sent to ${successCount} users`);
      setSelectedUsers([]);
      setBulkResetDialogOpen(false);
    } catch (err) {
      toast.error('Bulk reset failed');
    }
  };
  
  // Approval Actions
  const handleApproveRequest = async (requestId, data = {}) => {
    try {
      await api.teamRequests.approve(requestId, data);
      toast.success('Request approved and invitation sent');
      loadTeamRequests();
      loadInitialData();
      setApproveDialogOpen(false);
      setSelectedRequest(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve request');
    }
  };
  
  const handleRejectRequest = async (requestId, reason) => {
    try {
      await api.teamRequests.reject(requestId, { reason });
      toast.success('Request rejected');
      loadTeamRequests();
      loadInitialData();
      setRejectDialogOpen(false);
      setSelectedRequest(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject request');
    }
  };
  
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };
  
  const toggleProjectExpansion = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };
  
  // Badge Components
  const StatusBadge = ({ status }) => {
    const config = statusConfig[status] || { label: status || 'Unknown', color: 'bg-gray-100 text-gray-800' };
    const Icon = config.icon || CheckCircle;
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };
  
  const TeamBadge = ({ teamType }) => {
    if (!teamType) return null;
    const config = teamTypeConfig[teamType] || { label: teamType, color: 'bg-gray-100 text-gray-800' };
    return <Badge className={config.color}>{config.label}</Badge>;
  };
  
  // User Row Component
  const UserRow = ({ user }) => {
    const isSelected = selectedUsers.includes(user.id);
    const isCurrentUser = user.id === currentUser?.id;
    
    return (
      <tr className={`hover:bg-slate-50 ${isSelected ? 'bg-blue-50' : ''}`}>
        <td className="px-4 py-3">
          <Checkbox
            checked={isSelected}
            onCheckedChange={(checked) => {
              if (checked) {
                setSelectedUsers([...selectedUsers, user.id]);
              } else {
                setSelectedUsers(selectedUsers.filter(id => id !== user.id));
              }
            }}
            disabled={isCurrentUser}
          />
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-3">
            <div className={`rounded-full p-2 ${user.status === 'active' || user.is_active ? 'bg-green-100' : 'bg-slate-100'}`}>
              {user.status === 'active' || user.is_active ? (
                <UserCheck className="h-4 w-4 text-green-600" />
              ) : (
                <UserX className="h-4 w-4 text-slate-400" />
              )}
            </div>
            <div>
              <p className="font-medium text-slate-900">
                {user.first_name || user.name} {user.last_name || user.surname}
              </p>
              <p className="text-sm text-slate-500">{user.email}</p>
              {user.position && <p className="text-xs text-slate-400">{user.position}</p>}
            </div>
          </div>
        </td>
        <td className="px-4 py-3">
          <StatusBadge status={user.status || (user.is_active ? 'active' : 'disabled')} />
        </td>
        <td className="px-4 py-3">
          <TeamBadge teamType={user.team_type} />
        </td>
        <td className="px-4 py-3">
          <Badge variant="outline">{user.role_display_name || user.role || '-'}</Badge>
        </td>
        <td className="px-4 py-3">
          <div className="text-sm">
            {user.projects?.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {user.projects.slice(0, 2).map(p => (
                  <Badge key={p.id} variant="outline" className="text-xs">
                    {p.name}
                  </Badge>
                ))}
                {user.projects.length > 2 && (
                  <Badge variant="outline" className="text-xs">
                    +{user.projects.length - 2}
                  </Badge>
                )}
              </div>
            ) : user.project_ids?.length > 0 ? (
              <span className="text-slate-500">{user.project_ids.length} project(s)</span>
            ) : (
              <span className="text-slate-400">None</span>
            )}
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="text-sm text-slate-500">
            {user.invited_at ? format(new Date(user.invited_at), 'MMM d, yyyy') : 
             user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : '-'}
          </div>
        </td>
        <td className="px-4 py-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" data-testid={`user-actions-${user.id}`}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {/* Invite Actions */}
              {(user.status === 'pending' || user.status === 'expired') && (
                <>
                  <DropdownMenuItem onClick={() => handleResendInvite(user.id)}>
                    <Mail className="h-4 w-4 mr-2" />
                    Resend Invite
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleCopyInviteLink(user.id)}>
                    <Link2 className="h-4 w-4 mr-2" />
                    Copy Invite Link
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              
              {/* Edit Actions */}
              <DropdownMenuItem onClick={() => { setSelectedUser(user); setEditDialogOpen(true); }}>
                <Edit className="h-4 w-4 mr-2" />
                Edit User
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setSelectedUser(user); setProjectsDialogOpen(true); }}>
                <FolderOpen className="h-4 w-4 mr-2" />
                Manage Projects
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setSelectedUser(user); setPermissionsDialogOpen(true); }}>
                <Shield className="h-4 w-4 mr-2" />
                Edit Permissions
              </DropdownMenuItem>
              
              {/* Password Reset */}
              {(user.status === 'active' || user.is_active) && !isCurrentUser && (
                <DropdownMenuItem onClick={() => { setSelectedUser(user); setResetPasswordDialogOpen(true); }}>
                  <Key className="h-4 w-4 mr-2" />
                  Reset Password
                </DropdownMenuItem>
              )}
              
              <DropdownMenuSeparator />
              
              {/* Enable/Disable */}
              {!isCurrentUser && (
                <>
                  {user.status === 'disabled' || !user.is_active ? (
                    <DropdownMenuItem onClick={() => handleEnableUser(user.id)}>
                      <UserCheck className="h-4 w-4 mr-2" />
                      Enable User
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => handleDisableUser(user.id)} className="text-amber-600">
                      <UserX className="h-4 w-4 mr-2" />
                      Disable User
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => handleDeleteUser(user.id)} className="text-red-600">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete User
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </td>
      </tr>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-barlow font-bold uppercase tracking-wider text-slate-900">
              User Management
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              Manage users, invitations, and permissions
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setInviteDialogOpen(true)} data-testid="invite-user-btn" className="flex-1 sm:flex-none">
              <Send className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Invite User</span>
              <span className="sm:hidden">Invite</span>
            </Button>
            <Button size="sm" onClick={() => { setSelectedUser(null); setEditDialogOpen(true); }} data-testid="add-user-btn" className="flex-1 sm:flex-none">
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Add User</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </div>
        </div>
        
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-4">
            <div className="bg-white rounded-lg border p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-slate-500">Total Users</p>
              <p className="text-xl sm:text-2xl font-bold text-slate-900">{stats.total}</p>
            </div>
            <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-3 sm:p-4 cursor-pointer hover:bg-yellow-100" onClick={() => setActiveTab('pending')}>
              <p className="text-xs sm:text-sm text-yellow-700">Pending</p>
              <p className="text-xl sm:text-2xl font-bold text-yellow-800">{stats.by_status?.pending || 0}</p>
            </div>
            <div className="bg-green-50 rounded-lg border border-green-200 p-3 sm:p-4 cursor-pointer hover:bg-green-100" onClick={() => { setActiveTab('all'); setStatusFilter('active'); }}>
              <p className="text-xs sm:text-sm text-green-700">Active</p>
              <p className="text-xl sm:text-2xl font-bold text-green-800">{stats.by_status?.active || 0}</p>
            </div>
            <div className="bg-orange-50 rounded-lg border border-orange-200 p-3 sm:p-4 cursor-pointer hover:bg-orange-100" onClick={() => setActiveTab('expired')}>
              <p className="text-xs sm:text-sm text-orange-700">Expired</p>
              <p className="text-xl sm:text-2xl font-bold text-orange-800">{stats.by_status?.expired || 0}</p>
            </div>
            <div className="bg-red-50 rounded-lg border border-red-200 p-3 sm:p-4 cursor-pointer hover:bg-red-100 hidden sm:block" onClick={() => setActiveTab('deactivated')}>
              <p className="text-xs sm:text-sm text-red-700">Disabled</p>
              <p className="text-xl sm:text-2xl font-bold text-red-800">{stats.by_status?.disabled || 0}</p>
            </div>
            <div className="bg-blue-50 rounded-lg border border-blue-200 p-3 sm:p-4 hidden md:block">
              <p className="text-xs sm:text-sm text-blue-700">Professional</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-800">{stats.by_team?.professional || 0}</p>
            </div>
            <div className="bg-purple-50 rounded-lg border border-purple-200 p-3 sm:p-4 hidden lg:block">
              <p className="text-xs sm:text-sm text-purple-700">Invited (7d)</p>
              <p className="text-xl sm:text-2xl font-bold text-purple-800">{stats.recent_invites_7d || 0}</p>
            </div>
          </div>
        )}
        
        {/* Tabs - Scrollable on mobile */}
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex items-center gap-1 border-b min-w-max">
            <TabButton active={activeTab === 'all'} onClick={() => { setActiveTab('all'); setStatusFilter('all'); setPage(1); }}>
              All
            </TabButton>
            <TabButton active={activeTab === 'pending'} onClick={() => { setActiveTab('pending'); setPage(1); }} badge={stats?.by_status?.pending}>
              Pending
            </TabButton>
            <TabButton active={activeTab === 'approval'} onClick={() => { setActiveTab('approval'); setPage(1); }} badge={pendingRequestCount} badgeColor="blue">
              Approval
            </TabButton>
            <TabButton active={activeTab === 'expired'} onClick={() => { setActiveTab('expired'); setPage(1); }} badge={stats?.by_status?.expired} badgeColor="orange">
              Expired
            </TabButton>
            <TabButton active={activeTab === 'deactivated'} onClick={() => { setActiveTab('deactivated'); setPage(1); }} badge={stats?.by_status?.disabled} badgeColor="red">
              Disabled
            </TabButton>
          </div>
        </div>
        
        {/* Filters */}
        {activeTab !== 'approval' && (
          <div className="bg-white rounded-lg border p-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, email, company..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    className="pl-10"
                    data-testid="user-search"
                  />
                </div>
              </div>
              
              {/* Status Filter - only show on "All" tab */}
              {activeTab === 'all' && (
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              )}
              
              {/* Team Filter */}
              <Select value={teamFilter} onValueChange={(v) => { setTeamFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Teams</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="internal">Internal</SelectItem>
                  <SelectItem value="subcontractor">Subcontractor</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Role Filter */}
              <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {roles.map(r => (
                    <SelectItem key={r.name || r.role_id} value={r.name || r.role_id}>
                      {r.display_name || r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Project Filter */}
              <Select value={projectFilter} onValueChange={(v) => { setProjectFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Sort */}
              <Select 
                value={`${sortBy}-${sortOrder}`} 
                onValueChange={(v) => { 
                  const [field, order] = v.split('-');
                  setSortBy(field);
                  setSortOrder(order);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[160px]">
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at-desc">Newest First</SelectItem>
                  <SelectItem value="created_at-asc">Oldest First</SelectItem>
                  <SelectItem value="first_name-asc">Name (A-Z)</SelectItem>
                  <SelectItem value="first_name-desc">Name (Z-A)</SelectItem>
                  <SelectItem value="email-asc">Email (A-Z)</SelectItem>
                  <SelectItem value="status-asc">Status</SelectItem>
                  <SelectItem value="role-asc">Role</SelectItem>
                </SelectContent>
              </Select>
              
              {/* View Mode */}
              <div className="flex items-center gap-1 border rounded-md">
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="rounded-r-none"
                >
                  List
                </Button>
                <Button
                  variant={viewMode === 'grouped' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grouped')}
                  className="rounded-l-none"
                >
                  By Project
                </Button>
              </div>
              
              {/* Refresh */}
              <Button variant="outline" size="icon" onClick={refreshData}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Bulk Actions - Mobile-friendly */}
            {selectedUsers.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t">
                <span className="text-sm font-medium text-slate-700 mr-1">{selectedUsers.length} selected</span>
                
                {/* Mobile: Compact buttons with icons only */}
                <div className="flex items-center gap-1 sm:hidden">
                  <Button variant="outline" size="sm" onClick={() => setBulkAssignDialogOpen(true)} title="Assign to Project" className="px-2">
                    <FolderPlus className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setBulkResetDialogOpen(true)} title="Reset Passwords" className="px-2">
                    <Key className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleBulkAction('disable')} title="Disable" className="px-2">
                    <UserX className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleBulkAction('enable')} title="Enable" className="px-2">
                    <UserCheck className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 px-2" onClick={() => handleBulkAction('delete')} title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedUsers([])} className="px-2">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Desktop: Full buttons with labels */}
                <div className="hidden sm:flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setBulkAssignDialogOpen(true)}>
                    <FolderPlus className="h-4 w-4 mr-1" /> Assign to Project
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setBulkResetDialogOpen(true)}>
                    <Key className="h-4 w-4 mr-1" /> Reset Passwords
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleBulkAction('disable')}>
                    <UserX className="h-4 w-4 mr-1" /> Disable
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleBulkAction('enable')}>
                    <UserCheck className="h-4 w-4 mr-1" /> Enable
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleBulkAction('delete')}>
                    <Trash2 className="h-4 w-4 mr-1" /> Delete
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedUsers([])}>
                    Clear
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Content based on active tab */}
        {activeTab === 'approval' ? (
          <AwaitingApprovalContent
            loading={loading}
            teamRequests={teamRequests}
            onApprove={(req) => { setSelectedRequest(req); setApproveDialogOpen(true); }}
            onReject={(req) => { setSelectedRequest(req); setRejectDialogOpen(true); }}
          />
        ) : viewMode === 'grouped' ? (
          <GroupedUsersContent
            loading={loading}
            groupedData={groupedData}
            expandedProjects={expandedProjects}
            toggleProjectExpansion={toggleProjectExpansion}
            onUserAction={(user, action) => {
              setSelectedUser(user);
              if (action === 'edit') setEditDialogOpen(true);
              else if (action === 'projects') setProjectsDialogOpen(true);
              else if (action === 'permissions') setPermissionsDialogOpen(true);
              else if (action === 'resend') handleResendInvite(user.id);
              else if (action === 'disable') handleDisableUser(user.id);
              else if (action === 'enable') handleEnableUser(user.id);
            }}
          />
        ) : (
          <UsersTableContent
            loading={loading}
            users={users}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
            total={total}
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            sortBy={sortBy}
            sortOrder={sortOrder}
            handleSort={handleSort}
            UserRow={UserRow}
            currentUserId={currentUser?.id}
          />
        )}
        
        {/* Dialogs */}
        <InviteUserDialog
          open={inviteDialogOpen}
          onOpenChange={setInviteDialogOpen}
          projects={projects}
          roles={roles}
          onSuccess={refreshData}
        />
        
        <EditUserDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          user={selectedUser}
          projects={projects}
          roles={roles}
          onSuccess={refreshData}
        />
        
        <ManageProjectsDialog
          open={projectsDialogOpen}
          onOpenChange={setProjectsDialogOpen}
          user={selectedUser}
          projects={projects}
          onSuccess={refreshData}
        />
        
        <EditPermissionsDialog
          open={permissionsDialogOpen}
          onOpenChange={setPermissionsDialogOpen}
          user={selectedUser}
          onSuccess={refreshData}
        />
        
        <ResetPasswordDialog
          open={resetPasswordDialogOpen}
          onOpenChange={setResetPasswordDialogOpen}
          user={selectedUser}
          onConfirm={() => handleResetPassword(selectedUser?.id)}
        />
        
        <BulkResetPasswordDialog
          open={bulkResetDialogOpen}
          onOpenChange={setBulkResetDialogOpen}
          count={selectedUsers.length}
          onConfirm={handleBulkResetPassword}
        />
        
        <BulkAssignProjectsDialog
          open={bulkAssignDialogOpen}
          onOpenChange={setBulkAssignDialogOpen}
          selectedUserIds={selectedUsers}
          projects={projects}
          onSuccess={() => { loadDataForTab(); setSelectedUsers([]); }}
        />
        
        <InviteLinkDialog
          open={inviteLinkDialog.open}
          onOpenChange={(open) => setInviteLinkDialog({ ...inviteLinkDialog, open })}
          link={inviteLinkDialog.link}
          onCopy={() => copyToClipboard(inviteLinkDialog.link)}
        />
        
        <ApproveRequestDialog
          open={approveDialogOpen}
          onOpenChange={setApproveDialogOpen}
          request={selectedRequest}
          projects={projects}
          onApprove={handleApproveRequest}
        />
        
        <RejectRequestDialog
          open={rejectDialogOpen}
          onOpenChange={setRejectDialogOpen}
          request={selectedRequest}
          onReject={handleRejectRequest}
        />
      </div>
    </DashboardLayout>
  );
};

// Tab Button Component
const TabButton = ({ active, onClick, children, badge, badgeColor = 'yellow' }) => {
  const colorClasses = {
    yellow: 'bg-yellow-100 text-yellow-800',
    blue: 'bg-blue-100 text-blue-800',
    orange: 'bg-orange-100 text-orange-800',
    red: 'bg-red-100 text-red-800',
  };
  
  return (
    <button
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
        active 
          ? 'border-orange-500 text-orange-600' 
          : 'border-transparent text-slate-500 hover:text-slate-700'
      }`}
      onClick={onClick}
    >
      {children}
      {badge > 0 && (
        <Badge className={`${colorClasses[badgeColor]} h-5 min-w-[20px] justify-center text-xs`}>
          {badge}
        </Badge>
      )}
    </button>
  );
};

// Awaiting Approval Content
const AwaitingApprovalContent = ({ loading, teamRequests, onApprove, onReject }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }
  
  if (teamRequests.length === 0) {
    return (
      <div className="bg-white rounded-lg border p-12 text-center">
        <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-4" />
        <p className="text-slate-500">No pending approval requests</p>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-lg border overflow-hidden divide-y">
      {teamRequests.map(request => (
        <div key={request.id} className="p-4 hover:bg-slate-50">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h4 className="font-medium text-slate-900">
                  {request.first_name} {request.last_name}
                </h4>
                <Badge className={teamTypeConfig[request.team_type]?.color || 'bg-gray-100'}>
                  {teamTypeConfig[request.team_type]?.label || request.team_type}
                </Badge>
                <Badge variant="outline">{request.role || 'Team Member'}</Badge>
              </div>
              <p className="text-sm text-slate-500 mb-2">{request.email}</p>
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Building2 className="h-4 w-4" />
                  Project: <strong>{request.project_name}</strong>
                </span>
                {request.company_name && <span>Company: {request.company_name}</span>}
              </div>
              {request.reason && (
                <p className="text-sm text-slate-600 mt-2 bg-slate-50 p-2 rounded">
                  <strong>Reason:</strong> {request.reason}
                </p>
              )}
              <p className="text-xs text-slate-400 mt-2">
                Requested by {request.requested_by_name} on {format(new Date(request.created_at), 'MMM d, yyyy h:mm a')}
              </p>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <Button size="sm" onClick={() => onApprove(request)} className="bg-green-600 hover:bg-green-700">
                <CheckCircle className="h-4 w-4 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="outline" className="text-red-600" onClick={() => onReject(request)}>
                <XCircle className="h-4 w-4 mr-1" /> Reject
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Users Table Content
const UsersTableContent = ({ loading, users, selectedUsers, setSelectedUsers, total, page, setPage, totalPages, sortBy, sortOrder, handleSort, UserRow, currentUserId }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left w-10">
                <Checkbox
                  checked={selectedUsers.length === users.filter(u => u.id !== currentUserId).length && users.length > 0}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedUsers(users.filter(u => u.id !== currentUserId).map(u => u.id));
                    } else {
                      setSelectedUsers([]);
                    }
                  }}
                />
              </th>
              <SortableHeader label="User" field="first_name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
              <SortableHeader label="Status" field="status" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
              <SortableHeader label="Team" field="team_type" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
              <SortableHeader label="Role" field="role" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Projects</th>
              <SortableHeader label="Created" field="created_at" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {users.map(user => (
              <UserRow key={user.id} user={user} />
            ))}
          </tbody>
        </table>
      </div>
      
      {users.length === 0 && (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No users found</p>
        </div>
      )}
      
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <span className="text-sm text-slate-500">
            Showing {((page - 1) * 50) + 1} - {Math.min(page * 50, total)} of {total}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
              Previous
            </Button>
            <span className="text-sm">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Grouped Users Content
const GroupedUsersContent = ({ loading, groupedData, expandedProjects, toggleProjectExpansion, onUserAction }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }
  
  if (!groupedData?.projects?.length) {
    return (
      <div className="bg-white rounded-lg border p-12 text-center">
        <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">No users found</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {groupedData.projects.map(project => (
        <div key={project.project_id || 'unassigned'} className="bg-white rounded-lg border">
          <button
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50"
            onClick={() => toggleProjectExpansion(project.project_id)}
          >
            <div className="flex items-center gap-3">
              {expandedProjects[project.project_id] ? (
                <ChevronDown className="h-5 w-5 text-slate-400" />
              ) : (
                <ChevronRight className="h-5 w-5 text-slate-400" />
              )}
              <span className="font-medium text-slate-900">{project.project_name}</span>
              <Badge variant="outline">{project.total_users} users</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-green-100 text-green-800">{project.status_summary?.active || 0} active</Badge>
              <Badge className="bg-yellow-100 text-yellow-800">{project.status_summary?.pending || 0} pending</Badge>
            </div>
          </button>
          
          {expandedProjects[project.project_id] && (
            <div className="border-t px-4 py-4 space-y-4">
              {['professional', 'internal', 'subcontractor'].map(teamType => {
                const team = project.teams[teamType];
                if (!team || team.count === 0) return null;
                
                return (
                  <div key={teamType}>
                    <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                      <Badge className={teamTypeConfig[teamType]?.color}>{teamTypeConfig[teamType]?.label}</Badge>
                      <span>({team.count})</span>
                    </h4>
                    <div className="grid gap-2">
                      {team.users.map(user => (
                        <div key={user.id} className="flex items-center justify-between p-3 bg-slate-50 rounded">
                          <div>
                            <p className="font-medium text-sm">{user.first_name || user.name} {user.last_name || user.surname}</p>
                            <p className="text-xs text-slate-500">{user.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={statusConfig[user.status]?.color || 'bg-gray-100'}>
                              {statusConfig[user.status]?.label || user.status}
                            </Badge>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm"><MoreVertical className="h-4 w-4" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => onUserAction(user, 'edit')}>
                                  <Edit className="h-4 w-4 mr-2" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onUserAction(user, 'projects')}>
                                  <FolderOpen className="h-4 w-4 mr-2" /> Projects
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onUserAction(user, 'permissions')}>
                                  <Shield className="h-4 w-4 mr-2" /> Permissions
                                </DropdownMenuItem>
                                {user.status === 'pending' && (
                                  <DropdownMenuItem onClick={() => onUserAction(user, 'resend')}>
                                    <Mail className="h-4 w-4 mr-2" /> Resend Invite
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Dialog Components
const InviteUserDialog = ({ open, onOpenChange, projects, roles, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '', first_name: '', last_name: '', team_type: 'internal', role: '', company_name: '', discipline: '', project_ids: [],
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.invitations.invite(formData);
      toast.success('Invitation sent successfully');
      onOpenChange(false);
      onSuccess();
      setFormData({ email: '', first_name: '', last_name: '', team_type: 'internal', role: '', company_name: '', discipline: '', project_ids: [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    }
    setLoading(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Invite User</DialogTitle>
          <DialogDescription>Send an invitation email to a new team member</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>First Name *</Label><Input value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required /></div>
            <div><Label>Last Name *</Label><Input value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required /></div>
          </div>
          <div><Label>Email *</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required /></div>
          <div><Label>Team Type</Label>
            <Select value={formData.team_type} onValueChange={(v) => setFormData({ ...formData, team_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="professional">Professional Team</SelectItem>
                <SelectItem value="internal">Internal Team</SelectItem>
                <SelectItem value="subcontractor">Subcontractor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Role</Label>
            <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
              <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {['internal', 'professional', 'system'].map(cat => {
                  const catRoles = roles.filter(r => (r.category || 'system') === cat || (cat === 'system' && r.name === 'admin'));
                  if (catRoles.length === 0) return null;
                  const label = cat === 'internal' ? 'Internal Team' : cat === 'professional' ? 'Professional Team' : 'System';
                  return (
                    <React.Fragment key={cat}>
                      <div className="px-2 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-400 bg-slate-50">{label}</div>
                      {catRoles.map(r => <SelectItem key={r.name || r.role_id} value={r.name || r.role_id}>{r.display_name || r.name}</SelectItem>)}
                    </React.Fragment>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Company Name</Label><Input value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} placeholder="e.g., KZN Joinery (Pty) Ltd" /></div>
          <div><Label>Discipline / Trade</Label><Input value={formData.discipline} onChange={(e) => setFormData({ ...formData, discipline: e.target.value })} placeholder="e.g., Joinery, Electrical, Plumbing" /></div>
          <div><Label>Project</Label>
            <Select value={formData.project_ids[0] || ''} onValueChange={(v) => setFormData({ ...formData, project_ids: [v] })}>
              <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={loading}>{loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Send Invitation</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EditUserDialog = ({ open, onOpenChange, user, projects, roles, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || user.name || '',
        last_name: user.last_name || user.surname || '',
        email: user.email || '',
        role: user.role || '',
        team_type: user.team_type || '',
        company_name: user.company_name || '',
        discipline: user.discipline || '',
        position: user.position || '',
        phone: user.phone || '',
        project_ids: user.project_ids || [],
      });
    } else {
      setFormData({ first_name: '', last_name: '', email: '', role: '', team_type: 'internal', company_name: '', discipline: '', position: '', phone: '', project_ids: [] });
    }
  }, [user]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (user) {
        await api.adminUsers.update(user.id, formData);
        toast.success('User updated');
      } else {
        await api.post('/users', { ...formData, name: formData.first_name, surname: formData.last_name });
        toast.success('User created');
      }
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save user');
    }
    setLoading(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{user ? 'Edit User' : 'Add User'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>First Name</Label><Input value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} /></div>
            <div><Label>Last Name</Label><Input value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} /></div>
          </div>
          <div><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} disabled={!!user} /></div>
          <div><Label>Team Type</Label>
            <Select value={formData.team_type} onValueChange={(v) => setFormData({ ...formData, team_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="internal">Internal</SelectItem>
                <SelectItem value="subcontractor">Subcontractor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Role</Label>
            <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {['internal', 'professional', 'system'].map(cat => {
                  const catRoles = roles.filter(r => (r.category || 'system') === cat || (cat === 'system' && r.name === 'admin'));
                  if (catRoles.length === 0) return null;
                  const label = cat === 'internal' ? 'Internal Team' : cat === 'professional' ? 'Professional Team' : 'System';
                  return (
                    <React.Fragment key={cat}>
                      <div className="px-2 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-400 bg-slate-50">{label}</div>
                      {catRoles.map(r => <SelectItem key={r.name || r.role_id} value={r.name || r.role_id}>{r.display_name || r.name}</SelectItem>)}
                    </React.Fragment>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Company Name</Label><Input value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} placeholder="e.g., KZN Joinery (Pty) Ltd" /></div>
          <div><Label>Discipline / Trade</Label><Input value={formData.discipline} onChange={(e) => setFormData({ ...formData, discipline: e.target.value })} placeholder="e.g., Joinery, Electrical, Plumbing" /></div>
          <div><Label>Position</Label><Input value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} placeholder="e.g., Senior Site Agent" /></div>
          <div><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></div>
          <div><Label>Projects</Label>
            <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
              {projects.map(p => (
                <label key={p.id} className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox checked={formData.project_ids?.includes(p.id)} onCheckedChange={(c) => setFormData({ ...formData, project_ids: c ? [...(formData.project_ids || []), p.id] : formData.project_ids?.filter(id => id !== p.id) })} />
                  {p.name}
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={loading}>{loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ManageProjectsDialog = ({ open, onOpenChange, user, projects: projectsProp, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [localProjects, setLocalProjects] = useState([]);
  
  useEffect(() => { if (user) setSelected(user.project_ids || []); }, [user]);
  
  // Fallback: load projects if none passed from parent
  useEffect(() => {
    if (open && projectsProp.length === 0) {
      api.projects.getAll().then(res => setLocalProjects(res.data || [])).catch(() => {});
    }
  }, [open, projectsProp]);
  
  const projects = projectsProp.length > 0 ? projectsProp : localProjects;
  
  useEffect(() => { if (user) setSelected(user.project_ids || []); }, [user]);
  
  const handleSave = async () => {
    if (!user) return;
    setLoading(true);
    try {
      await api.adminUsers.update(user.id, { project_ids: selected });
      toast.success('Projects updated');
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      toast.error('Failed to update projects');
    }
    setLoading(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Manage Projects for {user?.first_name || user?.name}</DialogTitle></DialogHeader>
        <div className="max-h-64 overflow-y-auto border rounded p-3 space-y-2">
          {projects.map(p => (
            <label key={p.id} className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={selected.includes(p.id)} onCheckedChange={(c) => setSelected(c ? [...selected, p.id] : selected.filter(id => id !== p.id))} />
              <span>{p.name}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading}>{loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EditPermissionsDialog = ({ open, onOpenChange, user, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [rolePermissions, setRolePermissions] = useState({});
  const [roleData, setRoleData] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [activeTab, setActiveTab] = useState('custom');
  
  // Load user's custom permissions and role defaults
  useEffect(() => { 
    if (user) {
      setPermissions(user.custom_permissions || {});
      loadRoleDefaults(user.role);
    }
  }, [user]);

  const loadRoleDefaults = async (roleName) => {
    if (!roleName) return;
    try {
      const res = await api.get(`/roles/${roleName}`);
      setRoleData(res.data);
      setRolePermissions(res.data.permissions || {});
    } catch (err) {
      console.error('Failed to load role defaults:', err);
    }
  };
  
  const handleSaveCustom = async () => {
    if (!user) return;
    setLoading(true);
    try {
      await api.put(`/users/${user.id}/permissions`, { custom_permissions: permissions });
      toast.success('Custom permissions updated');
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      toast.error('Failed to update permissions');
    }
    setLoading(false);
  };

  const handleSaveRoleDefaults = async () => {
    if (!roleData) return;
    setSavingRole(true);
    try {
      await api.users.updateRole(roleData.name, { permissions: rolePermissions });
      toast.success(`Role "${roleData.display_name}" defaults updated`);
      onSuccess();
      onOpenChange(false); // Close dialog after successful save
    } catch (err) {
      console.error('Failed to update role defaults:', err);
      toast.error('Failed to update role defaults');
    }
    setSavingRole(false);
  };

  // Permission modules configuration
  const modules = [
    { key: 'delay_events', label: 'Delay Events', fields: ['view', 'create', 'edit', 'delete', 'approve', 'convert_to_notice'] },
    { key: 'notices', label: 'Notices', fields: ['view', 'create', 'edit', 'delete', 'share', 'convert_to_claim'] },
    { key: 'claims', label: 'EOT Claims', fields: ['view', 'create', 'edit', 'delete', 'submit'] },
    { key: 'rfis', label: 'RFIs', fields: ['view', 'create', 'edit', 'delete', 'issue', 'respond', 'close'] },
    { key: 'site_instructions', label: 'Site Instructions', fields: ['view', 'create', 'edit', 'delete', 'respond', 'close'] },
    { key: 'variation_orders', label: 'Variation Orders', fields: ['view', 'create', 'edit', 'delete', 'approve', 'submit'] },
    { key: 'irs', label: 'IRS & Timing Schedule', fields: ['view', 'create', 'edit', 'delete', 'upload', 'analyze', 'export'] },
    { key: 'shop_drawings', label: 'Shop Drawings', fields: ['view', 'create', 'edit', 'delete', 'submit', 'review', 'approve'] },
    { key: 'meeting_minutes', label: 'Meeting Minutes', fields: ['view', 'create', 'edit', 'delete', 'distribute', 'extract'] },
    { key: 'procurement', label: 'Procurement Schedule', fields: ['view', 'create', 'edit', 'delete', 'import_data', 'export'] },
    { key: 'tasks', label: 'Tasks', fields: ['view', 'create', 'edit', 'delete', 'assign', 'complete'] },
    { key: 'programs', label: 'Programs', fields: ['view', 'upload', 'analyze', 'compare', 'delete'] },
    { key: 'drawings', label: 'Drawings', fields: ['view', 'upload', 'edit', 'delete', 'distribute'] },
    { key: 'subcontractors', label: 'Subcontractors', fields: ['view', 'create', 'edit', 'delete', 'request_program'] },
    { key: 'document_control', label: 'Document Control', fields: ['view', 'upload', 'verify', 'reject', 'edit', 'delete', 'manage_access_records', 'mark_project_live'] },
    { key: 'disputes', label: 'Dispute Resolution', fields: ['view', 'create', 'edit', 'delete', 'manage_evidence', 'submit_decision', 'settle'] },
    { key: 'closeout', label: 'Close-Out Engine', fields: ['view', 'create', 'edit', 'delete', 'approve', 'manage_defects', 'sign_off'] },
    { key: 'reports', label: 'Reports', fields: ['view', 'generate', 'export'] },
  ];

  const adminPermissions = [
    { key: 'manage_users', label: 'Manage Users' },
    { key: 'manage_clients', label: 'Manage Clients' },
    { key: 'manage_projects', label: 'Manage Projects' },
    { key: 'view_all_projects', label: 'View All Projects' },
    { key: 'system_settings', label: 'System Settings' },
  ];

  const toggleModule = (key) => setExpandedModules(prev => ({ ...prev, [key]: !prev[key] }));

  const handleModulePermission = (moduleKey, field, value) => {
    setPermissions(prev => ({
      ...prev,
      [moduleKey]: { ...(prev[moduleKey] || {}), [field]: value }
    }));
  };

  const handleRoleModulePermission = (moduleKey, field, value) => {
    setRolePermissions(prev => ({
      ...prev,
      [moduleKey]: { ...(prev[moduleKey] || {}), [field]: value }
    }));
  };

  const handleAdminPermission = (key, value) => {
    setPermissions(prev => ({ ...prev, [key]: value }));
  };

  const handleRoleAdminPermission = (key, value) => {
    setRolePermissions(prev => ({ ...prev, [key]: value }));
  };

  const getPermissionValue = (moduleKey, field) => {
    return permissions?.[moduleKey]?.[field] || false;
  };

  const getRolePermissionValue = (moduleKey, field) => {
    return rolePermissions?.[moduleKey]?.[field] || false;
  };

  // Render permission modules for either custom or role defaults
  const renderPermissionModules = (isRoleDefaults = false) => {
    const perms = isRoleDefaults ? rolePermissions : permissions;
    const handleModule = isRoleDefaults ? handleRoleModulePermission : handleModulePermission;
    const handleAdmin = isRoleDefaults ? handleRoleAdminPermission : handleAdminPermission;
    const getPerm = isRoleDefaults ? getRolePermissionValue : getPermissionValue;

    return (
      <div className="space-y-3">
        {modules.map((module) => {
          const isHidden = getPerm(module.key, 'hidden');
          return (
            <div key={module.key} className={`border rounded-lg overflow-hidden ${isHidden ? 'border-red-200 bg-red-50/30' : ''}`}>
              <button
                type="button"
                onClick={() => toggleModule(`${isRoleDefaults ? 'role_' : ''}${module.key}`)}
                className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${isHidden ? 'bg-red-50 hover:bg-red-100' : 'bg-slate-50 hover:bg-slate-100'}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${isHidden ? 'text-red-700' : 'text-slate-700'}`}>{module.label}</span>
                  {isHidden && <Badge className="bg-red-100 text-red-700 text-xs">Hidden</Badge>}
                </div>
                {expandedModules[`${isRoleDefaults ? 'role_' : ''}${module.key}`] ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
              </button>
              {expandedModules[`${isRoleDefaults ? 'role_' : ''}${module.key}`] && (
                <div className="p-4 bg-white">
                  {/* Hidden Toggle */}
                  <div className="mb-4 pb-3 border-b border-red-100">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <Checkbox
                        checked={isHidden}
                        onCheckedChange={(checked) => handleModule(module.key, 'hidden', checked)}
                        className="border-red-300 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                      />
                      <div>
                        <span className="font-medium text-red-700">Hidden</span>
                        <p className="text-xs text-red-500">Completely hide this module from {isRoleDefaults ? 'users with this role' : 'this user'}</p>
                      </div>
                    </label>
                  </div>
                  
                  <div className={`grid grid-cols-2 sm:grid-cols-3 gap-3 ${isHidden ? 'opacity-40 pointer-events-none' : ''}`}>
                    {module.fields.map((field) => (
                      <label key={field} className="flex items-center gap-2 cursor-pointer text-sm">
                        <Checkbox
                          checked={getPerm(module.key, field)}
                          onCheckedChange={(checked) => handleModule(module.key, field, checked)}
                          disabled={isHidden}
                        />
                        <span className="capitalize">{field.replace(/_/g, ' ')}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Admin Permissions */}
        <div className="border border-amber-200 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => toggleModule(`${isRoleDefaults ? 'role_' : ''}admin`)}
            className="w-full px-4 py-3 flex items-center justify-between bg-amber-50 hover:bg-amber-100 transition-colors"
          >
            <span className="font-medium text-amber-800">Admin Permissions</span>
            {expandedModules[`${isRoleDefaults ? 'role_' : ''}admin`] ? <ChevronUp className="h-4 w-4 text-amber-600" /> : <ChevronDown className="h-4 w-4 text-amber-600" />}
          </button>
          {expandedModules[`${isRoleDefaults ? 'role_' : ''}admin`] && (
            <div className="p-4 grid grid-cols-2 gap-3 bg-amber-50/50">
              {adminPermissions.map((perm) => (
                <label key={perm.key} className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox
                    checked={perms?.[perm.key] || false}
                    onCheckedChange={(checked) => handleAdmin(perm.key, checked)}
                  />
                  <span>{perm.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Edit Permissions for {user?.first_name || user?.name}</DialogTitle>
          <p className="text-sm text-slate-500">
            Role: <Badge variant="outline" className="ml-1">{roleData?.display_name || user?.role}</Badge>
          </p>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex border-b mb-4">
          <button
            onClick={() => setActiveTab('custom')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'custom' 
                ? 'border-blue-500 text-blue-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Custom Permissions
          </button>
          <button
            onClick={() => setActiveTab('role')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'role' 
                ? 'border-purple-500 text-purple-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Role Defaults ({roleData?.display_name || user?.role})
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto pr-2">
          {activeTab === 'custom' ? (
            <div>
              <p className="text-xs text-slate-500 mb-3 bg-blue-50 p-2 rounded">
                Custom permissions override the role defaults for this specific user only.
              </p>
              {renderPermissionModules(false)}
            </div>
          ) : (
            <div>
              <p className="text-xs text-slate-500 mb-3 bg-purple-50 p-2 rounded">
                <strong>Warning:</strong> Changes here affect ALL users with the "{roleData?.display_name || user?.role_display_name || user?.role}" role.
              </p>
              {renderPermissionModules(true)}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {activeTab === 'custom' ? (
            <Button onClick={handleSaveCustom} disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Custom Permissions
            </Button>
          ) : (
            <Button onClick={handleSaveRoleDefaults} disabled={savingRole} className="bg-purple-600 hover:bg-purple-700">
              {savingRole && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Update Role Defaults
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ResetPasswordDialog = ({ open, onOpenChange, user, onConfirm }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader><DialogTitle>Reset Password</DialogTitle></DialogHeader>
      <p>Send a password reset email to <strong>{user?.email}</strong>?</p>
      <p className="text-sm text-slate-500">They will receive a link to create a new password.</p>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onConfirm}><Key className="h-4 w-4 mr-2" />Send Reset Email</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const BulkResetPasswordDialog = ({ open, onOpenChange, count, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Bulk Password Reset</DialogTitle></DialogHeader>
        <p>Send password reset emails to <strong>{count} users</strong>?</p>
        <p className="text-sm text-slate-500">This is useful for security purposes when you need the whole team to reset their passwords.</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={async () => { setLoading(true); await onConfirm(); setLoading(false); }} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Key className="h-4 w-4 mr-2" />}
            Send {count} Reset Emails
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BulkAssignProjectsDialog = ({ open, onOpenChange, selectedUserIds, projects, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [mode, setMode] = useState('add');
  
  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedProjects([]);
      setMode('add');
    }
  }, [open]);
  
  const handleAssign = async () => {
    if (selectedProjects.length === 0) {
      toast.error('Please select at least one project');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.adminUsers.bulkAssignProjects({
        user_ids: selectedUserIds,
        project_ids: selectedProjects,
        mode: mode
      });
      toast.success(`Assigned ${res.data.successful} user(s) to project(s)`);
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      toast.error('Failed to assign projects');
    }
    setLoading(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign to Project</DialogTitle>
          <DialogDescription>
            Assign {selectedUserIds.length} selected user(s) to projects
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Mode Selection */}
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="mode"
                value="add"
                checked={mode === 'add'}
                onChange={() => setMode('add')}
                className="h-4 w-4"
              />
              <span className="text-sm">Add to existing</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="mode"
                value="replace"
                checked={mode === 'replace'}
                onChange={() => setMode('replace')}
                className="h-4 w-4"
              />
              <span className="text-sm">Replace all</span>
            </label>
          </div>
          
          {/* Project Selection */}
          <div className="max-h-48 overflow-y-auto border rounded p-3 space-y-2">
            {projects.length === 0 ? (
              <p className="text-sm text-slate-500">No projects available</p>
            ) : (
              projects.map(p => (
                <label key={p.id} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox 
                    checked={selectedProjects.includes(p.id)} 
                    onCheckedChange={(c) => setSelectedProjects(c ? [...selectedProjects, p.id] : selectedProjects.filter(id => id !== p.id))} 
                  />
                  <span className="text-sm">{p.name}</span>
                </label>
              ))
            )}
          </div>
          
          {selectedProjects.length > 0 && (
            <p className="text-xs text-slate-500">
              {selectedProjects.length} project(s) selected
            </p>
          )}
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={loading || selectedProjects.length === 0} className="w-full sm:w-auto">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <FolderPlus className="h-4 w-4 mr-2" />
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const InviteLinkDialog = ({ open, onOpenChange, link, onCopy }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader><DialogTitle>Invite Link</DialogTitle><DialogDescription>Copy and share this link. Expires in 7 days.</DialogDescription></DialogHeader>
      <div className="flex items-center gap-2">
        <Input value={link} readOnly className="flex-1" />
        <Button onClick={onCopy}><Link2 className="h-4 w-4 mr-2" />Copy</Button>
      </div>
    </DialogContent>
  </Dialog>
);

const ApproveRequestDialog = ({ open, onOpenChange, request, projects, onApprove }) => {
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [role, setRole] = useState('');
  
  useEffect(() => { if (request) { setSelected([request.project_id]); setRole(request.role || ''); } }, [request]);
  
  if (!request) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Approve Request</DialogTitle></DialogHeader>
        <div className="bg-slate-50 rounded p-3 space-y-1 text-sm">
          <p><strong>Name:</strong> {request.first_name} {request.last_name}</p>
          <p><strong>Email:</strong> {request.email}</p>
          <p><strong>Requested by:</strong> {request.requested_by_name}</p>
        </div>
        <div><Label>Role</Label><Input value={role} onChange={(e) => setRole(e.target.value)} /></div>
        <div><Label>Projects</Label>
          <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
            {projects.map(p => (
              <label key={p.id} className="flex items-center gap-2 cursor-pointer text-sm">
                <Checkbox checked={selected.includes(p.id)} onCheckedChange={(c) => setSelected(c ? [...selected, p.id] : selected.filter(id => id !== p.id))} />
                {p.name}
              </label>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-green-600 hover:bg-green-700" disabled={loading || !selected.length} onClick={async () => { setLoading(true); await onApprove(request.id, { project_ids: selected, role }); setLoading(false); }}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Approve & Send Invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RejectRequestDialog = ({ open, onOpenChange, request, onReject }) => {
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  
  if (!request) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Reject Request</DialogTitle></DialogHeader>
        <div className="bg-slate-50 rounded p-3 text-sm">
          <p><strong>Name:</strong> {request.first_name} {request.last_name}</p>
          <p><strong>Requested by:</strong> {request.requested_by_name}</p>
        </div>
        <div><Label>Reason *</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Explain why..." className="min-h-[80px]" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" disabled={loading || !reason.trim()} onClick={async () => { setLoading(true); await onReject(request.id, reason); setLoading(false); setReason(''); }}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Reject
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminUserManagement;
